/**
 * GroupDetailsModal Component
 *
 * Modal for viewing group details including enriched user information.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useGroup } from '../hooks/useGroup';
import type { GroupSanitized } from '../../../types/group.types';
import { formatDate } from '../../../lib/utils/format';

interface GroupDetailsModalProps {
  group: GroupSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const GroupDetailsModal: React.FC<GroupDetailsModalProps> = ({
  group,
  tenantId,
  isOpen,
  onClose,
}) => {
  const { data: groupDetails, isLoading } = useGroup(
    tenantId,
    group?.groupId || ''
  );

  if (!group) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Group Details</Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Group Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{group.groupName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Group Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={group.groupCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Group ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={group.groupId} />
                  </dd>
                </div>
                {group.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{group.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Members */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Members ({groupDetails?.users.length || 0})
              </h3>
              {groupDetails && groupDetails.users.length > 0 ? (
                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                  {groupDetails.users.map((user) => (
                    <div key={user.userId} className="p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No members in this group</p>
              )}
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(group.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(group.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
